/**
 * @file     browserRuntime.ts
 * @purpose  Safe browser-environment accessors for sessionStorage, localStorage, location, document, and idle-scheduling, all guarded for SSR and React Native contexts.
 */
export type StorageDriver = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type CachedStorageState = StorageDriver | null | 'uninitialized';

let cachedSessionStorage: CachedStorageState = 'uninitialized';
let cachedLocalStorage: CachedStorageState = 'uninitialized';
const STORAGE_PROBE_KEY = '__relta_storage_probe__';

const getWindowObject = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window;
};

export const getSessionStorage = (): StorageDriver | null => {
  if (cachedSessionStorage !== 'uninitialized') {
    return cachedSessionStorage;
  }

  const windowObject = getWindowObject();

  if (!windowObject) {
    cachedSessionStorage = null;
    return null;
  }

  try {
    const storage = windowObject.sessionStorage;

    if (
      !storage ||
      typeof storage.getItem !== 'function' ||
      typeof storage.setItem !== 'function' ||
      typeof storage.removeItem !== 'function'
    ) {
      cachedSessionStorage = null;
      return null;
    }

    storage.setItem(STORAGE_PROBE_KEY, '1');
    storage.removeItem(STORAGE_PROBE_KEY);

    cachedSessionStorage = storage;
    return storage;
  } catch {
    cachedSessionStorage = null;
    return null;
  }
};

export const getLocalStorage = (): StorageDriver | null => {
  if (cachedLocalStorage !== 'uninitialized') {
    return cachedLocalStorage;
  }

  const windowObject = getWindowObject();

  if (!windowObject) {
    cachedLocalStorage = null;
    return null;
  }

  try {
    const storage = windowObject.localStorage;

    if (
      !storage ||
      typeof storage.getItem !== 'function' ||
      typeof storage.setItem !== 'function' ||
      typeof storage.removeItem !== 'function'
    ) {
      cachedLocalStorage = null;
      return null;
    }

    storage.setItem(STORAGE_PROBE_KEY, '1');
    storage.removeItem(STORAGE_PROBE_KEY);

    cachedLocalStorage = storage;
    return storage;
  } catch {
    cachedLocalStorage = null;
    return null;
  }
};

export const getLocationPathname = () => {
  const pathname = getWindowObject()?.location?.pathname;
  return typeof pathname === 'string' ? pathname : '/';
};

export const getLocationSearch = () => {
  const search = getWindowObject()?.location?.search;
  return typeof search === 'string' ? search : '';
};

export const getDocumentTitle = () => {
  if (typeof document === 'undefined') {
    return '';
  }

  return document.title ?? '';
};

export const getDocumentReferrer = () => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  return document.referrer || undefined;
};

export const scheduleIdleWork = (callback: () => void, timeout = 250): number => {
  const windowObject = getWindowObject();

  if (!windowObject) {
    return 0;
  }

  if (typeof windowObject.requestIdleCallback === 'function') {
    return windowObject.requestIdleCallback(() => callback(), { timeout });
  }

  return windowObject.setTimeout(callback, timeout);
};

export const cancelIdleWork = (handle: number) => {
  const windowObject = getWindowObject();

  if (!windowObject || !handle) {
    return;
  }

  if (typeof windowObject.cancelIdleCallback === 'function') {
    windowObject.cancelIdleCallback(handle);
    return;
  }

  windowObject.clearTimeout(handle);
};
